import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, Download, Share } from "lucide-react";

const STORAGE_KEY = "istanova_pwa_dismiss";
const DISMISS_DAYS = 30;

type BIPEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: "accepted" | "dismissed" }> };

const isStandalone = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(display-mode: standalone)").matches ||
    // @ts-expect-error iOS Safari
    window.navigator.standalone === true);

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !("MSStream" in window);

const shouldShow = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (!v) return true;
    const until = parseInt(v, 10);
    return Number.isNaN(until) || Date.now() > until;
  } catch {
    return true;
  }
};

export const InstallPrompt = () => {
  const { t } = useTranslation();
  const [evt, setEvt] = useState<BIPEvent | null>(null);
  const [showIOS, setShowIOS] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isStandalone() || !shouldShow()) return;

    // Wait 30s before showing
    const timer = setTimeout(() => {
      if (isIOS()) {
        setShowIOS(true);
        setOpen(true);
      }
    }, 30_000);

    const onBIP = (e: Event) => {
      e.preventDefault();
      setEvt(e as BIPEvent);
      setOpen(true);
    };
    window.addEventListener("beforeinstallprompt", onBIP);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("beforeinstallprompt", onBIP);
    };
  }, []);

  if (!open) return null;

  const dismissForever = () => {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now() + DISMISS_DAYS * 24 * 3600 * 1000));
    } catch { /* ignore */ }
    setOpen(false);
  };
  const dismissSession = () => setOpen(false);

  const onInstall = async () => {
    if (!evt) return;
    await evt.prompt();
    await evt.userChoice;
    setEvt(null);
    setOpen(false);
  };

  return (
    <div
      role="dialog"
      aria-label={t("pwa.title")}
      className="fixed bottom-24 inset-x-3 z-50 mx-auto max-w-md rounded-2xl border border-border bg-card p-4 shadow-premium animate-in fade-in slide-in-from-bottom-4 sm:bottom-6 sm:end-6 sm:start-auto"
    >
      <button
        type="button"
        onClick={dismissSession}
        aria-label={t("action.cancel")}
        className="absolute end-2 top-2 grid h-8 w-8 place-items-center rounded-full text-muted-foreground hover:bg-secondary"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="flex items-start gap-3 pe-6">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-gold-gradient text-primary-foreground">
          {showIOS ? <Share className="h-5 w-5" /> : <Download className="h-5 w-5" />}
        </span>
        <div className="flex-1">
          <p className="font-bold text-foreground">{t("pwa.title")}</p>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">
            {showIOS ? t("pwa.iosHint") : t("pwa.androidHint")}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {!showIOS && evt && (
              <button onClick={onInstall} className="rounded-full bg-primary px-4 py-1.5 text-xs font-bold text-primary-foreground">
                {t("pwa.install")}
              </button>
            )}
            <button onClick={dismissForever} className="rounded-full border border-border px-4 py-1.5 text-xs font-semibold text-muted-foreground hover:bg-secondary">
              {t("pwa.later")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
